<script>
  import { useQueryClient } from '@tanstack/svelte-query';
  import cls from 'classnames';
  import { sendLinkEditedDebugEvent } from './debug';
  import { Button, Input, Label } from 'flowbite-svelte';
  import { _ } from 'svelte-i18n';
  import Tags from './Tags.svelte';
  import { debounce } from './utils';
  import { createValidationState, outlineClasses } from './validation';
  import { onMount } from 'svelte';
  import LoadingSpinner from './LoadingSpinner.svelte';
  import { QUERY_KEY_SHORTLINK } from './constants';
  import { client } from './lib/client';

  const queryClient = useQueryClient();

  const ValidationState = {
    ...createValidationState(),
    isValid: true,
  };

  export let linkId;
  export let linkName;
  export let tags;
  export let onCancel = () => {};
  export let onSuccess = () => {};

  let tagsCopy;
  let nameValidationState = ValidationState;
  let isUpdatingShortlink = false;

  onMount(() => {
    tagsCopy = structuredClone(tags);
  });

  function displayError(err) {
    return (
      {
        'You already have a shortlink with this name': $_(
          'EditLinkForm.errors.duplicateName',
          {
            default: 'You already have a shortlink with this name',
          },
        ),
        'name must be between 1 and 100 characters': $_(
          'EditLinkForm.errors.invalidNameLength',
          {
            default: 'Name must be between 1 and 100 characters',
          },
        ),
      }[err] ??
      $_('EditLinkForm.errors.default', {
        default: 'An unexpected error occurred, try again',
      })
    );
  }

  function onNameInput() {
    nameValidationState = {
      ...ValidationState,
      ...{ isValid: linkName.length > 0 },
    };
  }

  function onNameValidationError(error) {
    nameValidationState = {
      isValid: false,
      message: displayError(error.message),
      inputClass: outlineClasses.red,
      outputClass:
        'visible text-btnInputError pl-6 bg-[url(/images/error.svg)] bg-no-repeat',
    };
  }

  function onUpdateLinkError(error) {
    isUpdatingShortlink = false;
    if (error.message.includes('name')) {
      onNameValidationError(error);
    }
  }

  function onUpdateLinkSuccess() {
    queryClient.invalidateQueries([QUERY_KEY_SHORTLINK, linkId]);
    onSuccess();
  }

  async function onSubmit() {
    isUpdatingShortlink = true;
    const response = await client.patch(`/api/shortlink/${linkId}`, {
      name: linkName,
      tags: tagsCopy,
    });

    if (response.status === 200) {
      sendLinkEditedDebugEvent(response.data.object);
      onUpdateLinkSuccess();
      return;
    }

    if (response.status === 400) {
      onUpdateLinkError(response.data.error);
    }
  }

  const tagsAutocomplete = debounce(async function tagsAutocomplete(search) {
    try {
      const response = await client.get(`/api/get-tags?search=${search}`);
      return response.data.objects;
    } catch (e) {
      return [];
    }
  }, 500);
</script>

<form class="space-y-4 lg:space-y-6" on:submit|preventDefault={onSubmit}>
  <div>
    <Label class="font-bold">
      {$_('EditLinkForm.linkName', { default: 'Link Name' })}
      <Input
        id="name-input"
        bind:value={linkName}
        on:input={onNameInput}
        placeholder={$_('EditLinkForm.namePlaceholder', {
          default: 'Name shortlink',
        })}
        class={cls('mt-2 font-normal', nameValidationState.inputClass)}
        required
      />
    </Label>
    <span
      class={cls('relative top-2 text-sm', nameValidationState.outputClass)}
    >
      {nameValidationState.message}
    </span>
  </div>
  <div class="w-full">
    <Tags
      placeholder={tags.length === 0 &&
        $_('EditLinkForm.labelsPlaceholder', {
          default: 'E.g. Spring Sale, Fashion',
        })}
      addKeys={[9, 13, 188]}
      allowBlur
      allowPaste
      onlyUnique
      autoComplete={tagsAutocomplete}
      autoCompleteKey={'tag_name'}
      autoCompleteShowKey={'tag_name'}
      autoCompleteMarkupKey={'tag_name'}
      maxTags={10}
      maxChars={100}
      bind:tags={tagsCopy}
    />
  </div>
  <div class="flex gap-2">
    <Button
      type="submit"
      class="mt-4"
      disabled={!nameValidationState.isValid || isUpdatingShortlink}
    >
      <LoadingSpinner isShowing={isUpdatingShortlink} />
      {isUpdatingShortlink
        ? $_('EditLinkForm.updating', {
            default: 'Updating...',
          })
        : $_('EditLinkForm.updateShortlink', {
            default: 'Update Shortlink',
          })}
    </Button>
    <Button class="mt-4" type="button" color="alternative" on:click={onCancel}>
      {$_('EditLinkForm.cancel', {
        default: 'Cancel',
      })}
    </Button>
  </div>
</form>
